const unresolvedPlaceholder = value =>
  typeof value === "string" && value.includes("[[") && value.includes("]]" );

const configuredCharacter = (value, fallback, allowEmpty = false) => {
  if (value === undefined || value === null || unresolvedPlaceholder(value)) {
    return fallback;
  }

  const character = String(value);
  return character || (allowEmpty ? "" : fallback);
};

export const getNumericConfig = component => {
  const parsedDecimalPlaces = Number(component?.iDecimalPlaces);
  const decimalPlaces = Number.isInteger(parsedDecimalPlaces) && parsedDecimalPlaces >= 0
    ? parsedDecimalPlaces
    : 0;
  const decimalChar = configuredCharacter(component?.sDecimalChar, ".");
  const configuredThousandSeparator = configuredCharacter(
    component?.sThousandSeparator,
    ",",
    true,
  );

  return {
    decimalPlaces,
    decimalChar,
    thousandSeparator:
      configuredThousandSeparator === decimalChar ? "" : configuredThousandSeparator,
    justify: String(component?.sJustify || "right").toLowerCase(),
  };
};

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const normalizeNumericValue = (value, config) => {
  const { decimalChar, thousandSeparator } = config;
  let normalized = String(value ?? "").trim();

  if (!normalized) return "0";

  // Values kept by the form are canonical (dot decimal). A configured decimal
  // character takes precedence only when it is actually present in the value.
  if (decimalChar !== "." && normalized.includes(decimalChar)) {
    if (thousandSeparator) {
      normalized = normalized.replace(
        new RegExp(escapeRegExp(thousandSeparator), "g"),
        "",
      );
    }
    normalized = normalized.replace(new RegExp(escapeRegExp(decimalChar), "g"), ".");
  } else if (thousandSeparator && thousandSeparator !== ".") {
    normalized = normalized.replace(
      new RegExp(escapeRegExp(thousandSeparator), "g"),
      "",
    );
  }

  normalized = normalized.replace(/[^0-9.-]/g, "");
  const negative = normalized.startsWith("-");
  normalized = normalized.replace(/-/g, "");

  const [integerPart = "0", ...fractionParts] = normalized.split(".");
  const fractionPart = fractionParts.join("");
  const canonical = `${integerPart || "0"}${fractionParts.length ? `.${fractionPart}` : ""}`;

  return `${negative ? "-" : ""}${canonical}`;
};

export const minorDigitsFromValue = (value, config) => {
  const { decimalPlaces } = config;
  const normalized = normalizeNumericValue(value, config);
  const numericValue = Number(normalized);
  const fixedValue = Number.isFinite(numericValue)
    ? Math.abs(numericValue).toFixed(decimalPlaces)
    : (0).toFixed(decimalPlaces);

  return fixedValue.replace(".", "").replace(/^0+(?=\d)/, "") || "0";
};

export const canonicalFromMinorDigits = (minorDigits, config) => {
  const { decimalPlaces } = config;
  const digits = String(minorDigits ?? "").replace(/\D/g, "") || "0";

  if (decimalPlaces === 0) {
    return digits.replace(/^0+(?=\d)/, "") || "0";
  }

  const padded = digits.padStart(decimalPlaces + 1, "0");
  const integerPart = padded.slice(0, -decimalPlaces).replace(/^0+(?=\d)/, "") || "0";
  const fractionPart = padded.slice(-decimalPlaces);

  return `${integerPart}.${fractionPart}`;
};

export const formatMinorDigits = (minorDigits, config) => {
  const { decimalPlaces, decimalChar, thousandSeparator } = config;
  const canonical = canonicalFromMinorDigits(minorDigits, config);
  const [integerPart, fractionPart] = canonical.split(".");
  const groupedInteger = thousandSeparator
    ? integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator)
    : integerPart;

  return decimalPlaces > 0
    ? `${groupedInteger}${decimalChar}${fractionPart}`
    : groupedInteger;
};

export const formatNumericValue = (value, componentOrConfig) => {
  const config = componentOrConfig?.decimalPlaces !== undefined
    ? componentOrConfig
    : getNumericConfig(componentOrConfig);

  return formatMinorDigits(minorDigitsFromValue(value, config), config);
};

export const canonicalNumericValue = (value, componentOrConfig) => {
  const config = componentOrConfig?.decimalPlaces !== undefined
    ? componentOrConfig
    : getNumericConfig(componentOrConfig);

  return canonicalFromMinorDigits(minorDigitsFromValue(value, config), config);
};
